import { AuthGuard } from "@elit-hair/elit-tools";
import { Button, DatePicker, EmptyState, ErrorState, Icon, Skeleton } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarTime, TbRefresh } from "react-icons/tb";
import z from "zod";

import { PageActions } from "@/components/page-actions";
import { AdminSurface } from "@/components/ui/admin-surface";
import { PERMISSIONS } from "@/constants/permissions";
import { getBrowserTimezone } from "@/features/schedule/utils/schedule-timezone.utils";
import { projectSlotsFromStorage } from "@/features/schedule/utils/slot-timezone.utils";
import { TimezoneSelect } from "@/pages/management/schedules/components/schedule-edit-views";
import { useEventType } from "@/queries/event-type.queries";
import { useEventTypeSlots, useRegenerateEventTypeSlots } from "@/queries/slot.queries";
import { APP_ROUTES_FULL } from "@/utils/routes";

import { BookableSlotsGrid } from "./components/bookable-slots-grid";
import { useBookableSlotsDateRange } from "./hooks/use-bookable-slots-date-range";

const SlotsSearchSchema = z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.EVENT_SLOTS)({
    component: BookableSlotsPage,
    validateSearch: zodValidator(SlotsSearchSchema),
});

function BookableSlotsPage() {
    const { t } = useTranslation();
    const { id: eventTypeId } = Route.useParams();
    const search = Route.useSearch({ structuralSharing: true });
    const navigate = Route.useNavigate();
    const regenerateSlotsMutation = useRegenerateEventTypeSlots();
    const [displayTimezone, setDisplayTimezone] = useState(getBrowserTimezone);

    const { data: eventType, isPending: isEventTypePending } = useEventType(eventTypeId);

    const updateSearch = useCallback(
        (updates: { startDate?: string; endDate?: string }) => {
            navigate({
                search: (prev) => ({ ...prev, ...updates }),
                replace: true,
            });
        },
        [navigate]
    );

    const { startDate, endDate, startDateStr, endDateStr, datesInRange, handleStartDateChange, handleEndDateChange } =
        useBookableSlotsDateRange({
            search,
            onSearchChange: updateSearch,
        });

    const {
        data: slots,
        isPending: isSlotsPending,
        isError,
        error,
        refetch,
    } = useEventTypeSlots(eventTypeId, startDateStr, endDateStr, { includeUnavailable: true });

    const displaySlots = useMemo(() => projectSlotsFromStorage(slots ?? {}, displayTimezone), [slots, displayTimezone]);

    const totalSlots = useMemo(
        () => datesInRange.reduce((sum, date) => sum + (displaySlots[date]?.length ?? 0), 0),
        [datesInRange, displaySlots]
    );

    const isLoading = isEventTypePending || isSlotsPending;

    const handleRegenerateSlots = () => {
        regenerateSlotsMutation.mutate(eventTypeId);
    };

    return (
        <div className="flex h-0 min-h-0 flex-1 flex-col gap-4">
            <AdminSurface className="flex shrink-0 flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                <div className="space-y-1">
                    <h2 className="text-base font-semibold tracking-tight">{t("bookable_slots")}</h2>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        {eventType && (
                            <>
                                <span className="rounded-lg border border-border/60 bg-muted/30 px-2 py-1">
                                    {t("max_booking_days_ahead")}: {eventType.maxBookingDaysAhead}
                                </span>
                                <span className="rounded-lg border border-border/60 bg-muted/30 px-2 py-1">
                                    {t("min_booking_notice_minutes")}: {eventType.minBookingNoticeMinutes}
                                </span>
                                {eventType.isActive === false && (
                                    <span className="rounded-lg border border-destructive/30 bg-destructive/10 px-2 py-1 text-destructive">
                                        {t("inactive")}
                                    </span>
                                )}
                            </>
                        )}
                        {!isLoading && (
                            <span className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-700 dark:text-emerald-400">
                                {t("bookable_slots_count", { count: totalSlots })}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <TimezoneSelect
                        value={displayTimezone}
                        onChange={setDisplayTimezone}
                        className="w-full max-w-56 sm:w-56"
                    />
                    <DatePicker date={startDate.toJSDate()} onDateChange={handleStartDateChange} />
                    <span className="hidden text-muted-foreground sm:inline">–</span>
                    <DatePicker date={endDate.toJSDate()} onDateChange={handleEndDateChange} />
                    <PageActions>
                        <AuthGuard required={[PERMISSIONS.CALL_SVC_ADMIN_BASE]}>
                            <Button
                                variant="outline"
                                onClick={handleRegenerateSlots}
                                disabled={regenerateSlotsMutation.isPending}
                            >
                                <Icon icon={TbRefresh} />
                                {regenerateSlotsMutation.isPending ? t("loading") : t("regenerate_slots")}
                            </Button>
                        </AuthGuard>
                    </PageActions>
                </div>
            </AdminSurface>

            {isError && (
                <ErrorState
                    title={t("failed_to_load_bookable_slots")}
                    message={error?.message || t("event_load_error")}
                    onRetry={refetch}
                />
            )}

            {isLoading && (
                <div className="grid min-h-0 flex-1 auto-rows-[minmax(17.5rem,20rem)] grid-cols-2 gap-2.5 overflow-y-auto md:grid-cols-3 lg:grid-cols-4 xl:h-full xl:auto-rows-fr xl:grid-cols-7 xl:overflow-hidden">
                    {Array.from({ length: datesInRange.length || 7 }).map((_, index) => (
                        <Skeleton key={index} className="min-h-70 w-full rounded-2xl xl:h-full xl:min-h-0" />
                    ))}
                </div>
            )}

            {!isLoading && !isError && datesInRange.length === 0 && (
                <AdminSurface className="p-8">
                    <EmptyState
                        variant="simple"
                        icon={<TbCalendarTime size={40} className="text-muted-foreground" />}
                        title="no_bookable_slots_found"
                    />
                </AdminSurface>
            )}

            {!isLoading && !isError && datesInRange.length > 0 && (
                <div className="flex h-0 min-h-0 flex-1 flex-col">
                    <BookableSlotsGrid datesInRange={datesInRange} slots={displaySlots} />
                </div>
            )}
        </div>
    );
}
